import {
	Box,
	SimpleGrid,
	Text,
} from '@chakra-ui/react';

import {
	ConfirmDialog,
} from './components';

import RescheduleDialog from '../detail/RescheduleDialog';

import {
	creatorOf,
	formatDate,
} from '../_lib/campaignState';

import {
	useCampaignUI,
} from './ui';

const COPY = {
	start: [
		'Launch this campaign?',
		'This prepares recipients and their delivery times. Sending happens automatically when each message is due. Launch only once.',
		'Launch campaign',
	],

	submit: [
		'Submit this campaign?',
		'The server will validate this draft and decide whether approval is required. This action does not send messages.',
		'Submit campaign',
	],

	approve: [
		'Approve this campaign?',
		'The campaign will become ready to launch. Approving does not start sending.',
		'Approve',
	],

	reject: [
		'Request changes?',
		'Your reason will be shown to the creator. The campaign returns to an editable draft.',
		'Request changes',
	],

	pause: [
		'Pause new sends?',
		'New sends will pause. A message already submitted to WhatsApp cannot be recalled.',
		'Pause campaign',
	],

	resume: [
		'Resume this campaign?',
		'Remaining messages will resume within their allowed delivery windows. Existing safety checks still apply.',
		'Resume campaign',
	],

	delete: [
		'Delete this campaign?',
		'Remove this unstarted campaign from your campaign list. You cannot restore it from this screen. Its audit record is retained; leads, consent records and WhatsApp conversations are not deleted.',
		'Delete campaign',
	],

	cancel: [
		'Cancel this campaign?',
		'Unsent work will be cancelled. Messages already submitted cannot be recalled. This cannot be undone.',
		'Cancel campaign',
	],

	duplicate: [
		'Create a new draft?',
		'This uses the existing duplicate API. Review its audience and settings before submitting. It is not launched automatically.',
		'Create draft',
	],

	resend: [
		'Build the next wave?',
		'Creates a new draft for reached contacts who did not reply. Review the message and schedule before submitting.',
		'Build next wave',
	],
};

export default function CampaignActionDialogs({
	manager,
}) {
	const ui = useCampaignUI();

	const dialog =
		manager.dialog;

	if (!dialog) {
		return null;
	}

	const {
		action,
		campaign,
	} = dialog;

	if (
		action ===
		'reschedule'
	) {
		return (
			<RescheduleDialog
				isOpen
				onClose={
					manager.closeDialog
				}
				campaign={
					campaign
				}
				isLoading={
					manager.isBusy
				}
				onConfirm={async (
					payload,
				) => {
					if (
						await manager.perform(
							action,
							campaign,
							payload,
						)
					) {
						manager.closeDialog();
					}
				}}
			/>
		);
	}

	const [
		title,
		description,
		label,
	] =
		COPY[action] ??
		COPY.start;

	return (
		<ConfirmDialog
			isOpen
			onClose={
				manager.closeDialog
			}
			title={title}
			description={
				description
			}
			confirmLabel={
				label
			}
			isLoading={
				manager.isBusy
			}
			confirmScheme={
				[
					'cancel',
					'reject',
					'delete',
				].includes(action)
					? 'red'
					: 'orange'
			}
			requireReason={
				action ===
				'reject'
			}
			showReason={
				[
					'cancel',
					'delete',
				].includes(action)
			}
			reasonLabel={
				action ===
				'reject'
					? 'What needs to change?'
					: 'Reason (optional)'
			}
			onConfirm={(
				reason,
			) =>
				manager.perform(
					action,
					campaign,
					reason
						? {
								reason,
							}
						: {},
				)
			}
		>
			<Box
				bg={
					ui.surfaceSubtle
				}
				p={4}
				borderRadius='xl'
			>
				<Text
					fontWeight='semibold'
					fontSize='sm'
					overflowWrap='anywhere'
				>
					{
						campaign.name
					}
				</Text>

				<SimpleGrid
					columns={{
						base: 1,
						sm: 2,
					}}
					spacing={3}
					mt={3}
				>
					<Box>
						<Text
							color={
								ui.textMuted
							}
							fontSize='xs'
						>
							Created by
						</Text>

						<Text fontSize='sm'>
							{
								creatorOf(
									campaign,
								).name
							}
						</Text>
					</Box>

					<Box>
						<Text
							color={
								ui.textMuted
							}
							fontSize='xs'
						>
							Delivery dates
						</Text>

						<Text fontSize='sm'>
							{formatDate(
								campaign
									.schedule
									?.startDate,
							)}

							{campaign
								.schedule
								?.endDate
								? ` - ${formatDate(
										campaign
											.schedule
											.endDate,
									)}`
								: ''}
						</Text>
					</Box>
				</SimpleGrid>

				{action ===
					'start' && (
					<Text
						fontSize='xs'
						mt={3}
						color={
							ui.textSecondary
						}
					>
						Delivery windows use each recipient&apos;s time zone. A future window will not send immediately.
					</Text>
				)}
			</Box>
		</ConfirmDialog>
	);
}